/** @format */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  ArrowLeftIcon,
  BookOpenIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
  TagIcon,
  UserIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Squares2X2Icon,
  ListBulletIcon,
  StarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useFirestore } from "../hooks/useFirestore";
import useBookStats from "../hooks/useBookStats";
import useBookReviewStats from "../hooks/useBookReviewStats";
import {
  getBookCategoryOptions,
  getBookSubcategoryOptions,
  getBookCategoryOptionsWithCustom,
  getBookSubcategoryOptionsWithCustom,
} from "../utils/categoryUtils";
import BookModal from "../components/BookModal";
import ConfirmDialog from "../components/ConfirmDialog";
import UserAssignmentModal from "../components/UserAssignmentModal";
import BookReviewsModal from "../components/BookReviewsModal";

const BooksPage = () => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [subcategoryFilter, setSubcategoryFilter] = useState("all");
  const [priceFilter, setPriceFilter] = useState("all");
  const [viewMode, setViewMode] = useState("cards"); // 'cards' or 'table'
  const [sortBy, setSortBy] = useState("title");
  const [sortDirection, setSortDirection] = useState("asc");
  const [userAssignmentModalOpen, setUserAssignmentModalOpen] = useState(false);
  const [selectedBookForAssignment, setSelectedBookForAssignment] =
    useState(null);
  const [reviewsModalOpen, setReviewsModalOpen] = useState(false);
  const [selectedBookForReviews, setSelectedBookForReviews] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [booksPerPage, setBooksPerPage] = useState(12); // Show 12 books per page (4 rows of 3 for large screens)

  const {
    documents: books,
    loading,
    addDocument,
    updateDocument,
    deleteDocument,
  } = useFirestore("books");

  // Get book statistics including user counts
  const {
    getUserCountForBook,
    refreshCounts,
    loading: statsLoading,
  } = useBookStats();

  // Get book review statistics
  const {
    getReviewCountForBook: getBookReviewCount,
    refreshCounts: refreshReviewCounts,
    loading: reviewStatsLoading,
  } = useBookReviewStats();

  // Get book categories including custom option
  const categories = getBookCategoryOptionsWithCustom();

  // Filter and sort books
  const filteredBooks = books.filter((book) => {
    const matchesSearch =
      !searchTerm ||
      book.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.author?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.category?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      categoryFilter === "all" || book.category === categoryFilter;

    const matchesSubcategory =
      subcategoryFilter === "all" || book.subcategory === subcategoryFilter;

    let matchesPrice = true;
    if (priceFilter === "free") {
      matchesPrice = parseFloat(book.amount) === 0;
    } else if (priceFilter === "paid") {
      matchesPrice = parseFloat(book.amount) > 0;
    } else if (priceFilter === "under10") {
      matchesPrice = parseFloat(book.amount) < 10;
    } else if (priceFilter === "under25") {
      matchesPrice = parseFloat(book.amount) < 25;
    }

    return (
      matchesSearch && matchesCategory && matchesSubcategory && matchesPrice
    );
  });

  const sortedBooks = [...filteredBooks].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];

    if (sortBy === "amount") {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedBooks.length / booksPerPage);
  const indexOfLastBook = currentPage * booksPerPage;
  const indexOfFirstBook = indexOfLastBook - booksPerPage;
  const currentBooks = sortedBooks.slice(indexOfFirstBook, indexOfLastBook);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    categoryFilter,
    subcategoryFilter,
    priceFilter,
    sortBy,
    sortDirection,
    booksPerPage,
  ]);

  // Keyboard navigation for pagination
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (totalPages <= 1) return;

      if (e.key === "ArrowLeft" && currentPage > 1) {
        e.preventDefault();
        goToPreviousPage();
      } else if (e.key === "ArrowRight" && currentPage < totalPages) {
        e.preventDefault();
        goToNextPage();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [currentPage, totalPages]);

  // Pagination handlers
  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Get unique categories from existing books
  const existingCategories = [
    ...new Set(books.map((book) => book.category).filter(Boolean)),
  ];

  // Get statistics
  const stats = {
    total: books.length,
    free: books.filter((book) => parseFloat(book.amount) === 0).length,
    paid: books.filter((book) => parseFloat(book.amount) > 0).length,
    categories: existingCategories.length,
  };

  const handleAdd = () => {
    setSelectedDocument(null);
    setModalMode("add");
    setModalOpen(true);
  };

  const handleEdit = (book) => {
    setSelectedDocument(book);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleView = (book) => {
    setSelectedDocument(book);
    setModalMode("view");
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteDocument(deleteId);
      setConfirmDialogOpen(false);
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting book:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSave = async (data) => {
    if (modalMode === "add") {
      await addDocument(data);
    } else if (modalMode === "edit") {
      await updateDocument(selectedDocument.id, data);
    }
    setModalOpen(false);
  };

  const handleManageUsers = (book) => {
    setSelectedBookForAssignment(book);
    setUserAssignmentModalOpen(true);
  };

  // Handle user assignment modal close and refresh counts
  const handleUserAssignmentModalClose = () => {
    setUserAssignmentModalOpen(false);
    setSelectedBookForAssignment(null);
    // Refresh the book user counts after modal closes
    refreshCounts();
  };

  const handleReviewsModalClose = () => {
    setReviewsModalOpen(false);
    setSelectedBookForReviews(null);
    // Refresh the review counts after modal closes
    refreshReviewCounts();
  };

  const handleManageReviews = (book) => {
    setSelectedBookForReviews(book);
    setReviewsModalOpen(true);
  };

  const formatPrice = (amount) => {
    const price = parseFloat(amount);
    if (price === 0) return "Free";
    return `GHS ${price.toFixed(2)}`;
  };

  const getBookCover = (book) => {
    if (book.image_url) {
      return (
        <img
          src={book.image_url}
          alt={book.title}
          className="book-cover w-full h-56 object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            e.target.style.display = "none";
            e.target.nextSibling.style.display = "flex";
          }}
        />
      );
    }
    return (
      <div className="w-full h-56 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center border-b border-gray-100">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3 shadow-inner">
            <BookOpenIcon className="h-10 w-10 text-blue-500" />
          </div>
          <p className="text-sm text-gray-500 font-medium">No Cover Image</p>
        </div>
      </div>
    );
  };

  const renderBookCard = (book) => (
    <div
      key={book.id}
      className="book-card group bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg hover:border-gray-200 transition-all duration-300 transform hover:-translate-y-1"
    >
      {/* Book Cover */}
      <div className="relative overflow-hidden">
        {getBookCover(book)}
        {/* Price Badge */}
        <div className="absolute top-3 right-3">
          <span
            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${
              parseFloat(book.amount) === 0
                ? "bg-emerald-500 text-white"
                : "bg-blue-500 text-white"
            }`}
          >
            {parseFloat(book.amount) === 0 ? "Free" : "Paid"}
          </span>
        </div>
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
            <div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-lg p-2 shadow-lg">
              <BookOpenIcon className="h-6 w-6 text-gray-700" />
            </div>
          </div>
        </div>
      </div>

      {/* Book Info */}
      <div className="p-5">
        {/* Title */}
        <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors duration-200">
          {book.title || "Untitled Book"}
        </h3>

        {/* Author */}
        {book.author && (
          <div className="flex items-center mb-3 text-sm text-gray-600">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
              <UserIcon className="h-4 w-4 text-blue-600" />
            </div>
            <span className="font-medium">{book.author}</span>
          </div>
        )}

        {/* Description */}
        {book.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-3 leading-relaxed">
            {book.description}
          </p>
        )}

        {/* Price and Stats */}
        <div className="mb-4">
          {/* Price */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-2xl font-bold text-emerald-600">
              {formatPrice(book.amount)}
            </span>
            {book.category && (
              <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                <TagIcon className="h-3 w-3 mr-1.5" />
                {book.category}
                {book.subcategory && (
                  <span className="ml-1 text-purple-600">
                    • {book.subcategory}
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                    <UserIcon className="h-3 w-3 text-blue-600" />
                  </div>
                  <span className="text-xs font-medium text-blue-700">
                    Users
                  </span>
                </div>
                <span className="text-sm font-bold text-blue-900">
                  {statsLoading ? (
                    <span className="inline-block w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></span>
                  ) : (
                    getUserCountForBook(book.id)
                  )}
                </span>
              </div>
            </div>

            <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center mr-2">
                    <StarIcon className="h-3 w-3 text-amber-600" />
                  </div>
                  <span className="text-xs font-medium text-amber-700">
                    Reviews
                  </span>
                </div>
                <span className="text-sm font-bold text-amber-900">
                  {reviewStatsLoading ? (
                    <span className="inline-block w-4 h-4 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin"></span>
                  ) : (
                    getBookReviewCount(book.id)
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => handleView(book)}
            className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-200 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
          >
            <EyeIcon className="h-4 w-4 mr-2" />
            View
          </button>
          <button
            onClick={() => handleEdit(book)}
            className="inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </button>
        </div>

        {/* Secondary Actions */}
        <div className="grid grid-cols-3 gap-2 mt-2">
          <button
            onClick={() => handleManageUsers(book)}
            className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-200"
          >
            <UserIcon className="h-3 w-3 mr-1" />
            Users
          </button>
          <button
            onClick={() => handleManageReviews(book)}
            className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs font-medium rounded-lg text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-200"
          >
            <StarIcon className="h-3 w-3 mr-1" />
            Reviews
          </button>
          <button
            onClick={() => handleDelete(book.id)}
            className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
          >
            <TrashIcon className="h-3 w-3 mr-1" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const renderBookTable = () => (
    <div className="bg-white shadow-xl overflow-hidden rounded-xl border border-gray-200">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Book
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Author
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Users
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reviews
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentBooks.map((book) => (
              <tr key={book.id} className="hover:bg-gray-50">
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-6 sm:h-12 sm:w-8">
                      {getBookCover(book)}
                    </div>
                    <div className="ml-2 sm:ml-4">
                      <div className="text-xs sm:text-sm font-medium text-gray-900">
                        {book.title || "Untitled Book"}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 line-clamp-2">
                        {book.description}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                  {book.author || "-"}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  {book.category ? (
                    <div className="flex flex-col space-y-1">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {book.category}
                      </span>
                      {book.subcategory && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {book.subcategory}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-green-600 price-badge">
                  {formatPrice(book.amount)}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <UserIcon className="h-3 w-3 mr-1" />
                    {statsLoading ? (
                      <span className="inline-block w-3 h-3 border border-blue-300 border-t-blue-600 rounded-full animate-spin"></span>
                    ) : (
                      getUserCountForBook(book.id)
                    )}
                  </span>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <StarIcon className="h-3 w-3 mr-1" />
                    {reviewStatsLoading ? (
                      <span className="inline-block w-3 h-3 border border-yellow-300 border-t-yellow-600 rounded-full animate-spin"></span>
                    ) : (
                      getBookReviewCount(book.id)
                    )}
                  </span>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-xs sm:text-sm font-medium">
                  <div className="flex justify-end space-x-1 sm:space-x-2">
                    <button
                      onClick={() => handleView(book)}
                      className="text-blue-600 hover:text-blue-900 p-1 sm:p-2"
                      title="View"
                    >
                      <EyeIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(book)}
                      className="text-indigo-600 hover:text-indigo-900 p-1 sm:p-2"
                      title="Edit"
                    >
                      <PencilIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button>
                    <button
                      onClick={() => handleManageUsers(book)}
                      className="text-green-600 hover:text-green-900 p-1 sm:p-2"
                      title="Manage Users"
                    >
                      <UserIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button>
                    <button
                      onClick={() => handleManageReviews(book)}
                      className="text-yellow-600 hover:text-yellow-900 p-1 sm:p-2"
                      title="Manage Reviews"
                    >
                      <StarIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(book.id)}
                      className="text-red-600 hover:text-red-900 p-1 sm:p-2"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={goToPreviousPage}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={goToNextPage}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-medium">{indexOfFirstBook + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(indexOfLastBook, sortedBooks.length)}
                </span>{" "}
                of <span className="font-medium">{sortedBooks.length}</span>{" "}
                results
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px pagination-controls"
                aria-label="Pagination"
              >
                <button
                  onClick={goToPreviousPage}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10"
                >
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => goToPage(i + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                      currentPage === i + 1
                        ? "z-10 bg-blue-500 text-white focus-visible:outline-none"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={goToNextPage}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10"
                >
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>

              {/* Go to page input */}
              <div className="ml-4 flex items-center space-x-2">
                <span className="text-sm text-gray-500">Go to:</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page >= 1 && page <= totalPages) {
                      goToPage(page);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      const page = parseInt(e.target.value);
                      if (page >= 1 && page <= totalPages) {
                        goToPage(page);
                      }
                    }
                  }}
                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-sm text-gray-500">of {totalPages}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div>
        {/* Header Skeleton */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center">
              <div className="mr-4 w-10 h-10 bg-gray-200 rounded-md animate-pulse"></div>
              <div>
                <div className="h-8 w-48 bg-gray-200 rounded-md animate-pulse mb-2"></div>
                <div className="h-4 w-64 bg-gray-200 rounded-md animate-pulse"></div>
              </div>
            </div>
            <div className="w-full sm:w-24 h-10 bg-gray-200 rounded-md animate-pulse"></div>
          </div>
        </div>

        {/* Stats Skeleton */}
        <div className="grid grid-cols-2 gap-3 sm:gap-5 sm:grid-cols-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-3 sm:p-5">
                <div className="flex items-center">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 rounded-full animate-pulse"></div>
                  <div className="ml-2 sm:ml-5 w-0 flex-1">
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse mb-2"></div>
                    <div className="h-6 w-16 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters Skeleton */}
        <div className="bg-white shadow rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col space-y-4">
            <div className="w-full">
              <div className="h-10 bg-gray-200 rounded-md animate-pulse"></div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-24 h-10 bg-gray-200 rounded-md animate-pulse"
                ></div>
              ))}
            </div>
          </div>
        </div>

        {/* Books Grid Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-6 lg:gap-8">
          {Array.from({ length: Math.min(booksPerPage, 12) }, (_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="w-full h-56 bg-gradient-to-r from-gray-200 to-gray-300 animate-pulse"></div>
              <div className="p-5">
                <div className="h-6 w-3/4 bg-gray-200 rounded-lg animate-pulse mb-3"></div>
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse mr-3"></div>
                  <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse mb-2"></div>
                <div className="h-4 w-2/3 bg-gray-200 rounded animate-pulse mb-4"></div>

                <div className="flex items-center justify-between mb-3">
                  <div className="h-8 w-20 bg-gray-200 rounded-lg animate-pulse"></div>
                  <div className="h-6 w-16 bg-gray-200 rounded-full animate-pulse"></div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-gray-100 rounded-lg p-3">
                    <div className="h-4 w-12 bg-gray-200 rounded animate-pulse mb-2"></div>
                    <div className="h-5 w-8 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-3">
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse mb-2"></div>
                    <div className="h-5 w-8 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                  <div className="h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/")}
              className="mr-4 p-2 rounded-md text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Books Management
              </h1>
              <p className="mt-1 text-sm text-gray-500 flex items-center">
                Manage your digital library and book collection
                {statsLoading && (
                  <span className="ml-2 inline-flex items-center text-blue-600">
                    <span className="inline-block w-3 h-3 border border-blue-300 border-t-blue-600 rounded-full animate-spin mr-1"></span>
                    Loading user stats...
                  </span>
                )}
                {totalPages > 1 && (
                  <span className="ml-2 inline-flex items-center text-gray-600 pagination-status">
                    <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-1"></span>
                    Pagination active ({totalPages} pages)
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={refreshCounts}
              disabled={statsLoading}
              className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh user counts"
            >
              <svg
                className={`h-4 w-4 mr-1 ${statsLoading ? "animate-spin" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {statsLoading ? "Refreshing..." : "Refresh Stats"}
            </button>
            <button
              onClick={handleAdd}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Book
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:gap-5 sm:grid-cols-4 mb-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-3 sm:p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <BookOpenIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                </div>
              </div>
              <div className="ml-2 sm:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">
                    Total Books
                  </dt>
                  <dd className="text-base sm:text-lg font-medium text-gray-900">
                    {stats.total}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-3 sm:p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                </div>
              </div>
              <div className="ml-2 sm:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">
                    Free Books
                  </dt>
                  <dd className="text-base sm:text-lg font-medium text-gray-900">
                    {stats.free}
                  </dd>
                  {stats.total > 0 && (
                    <div className="mt-1">
                      <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                        <div
                          className="bg-green-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${(stats.free / stats.total) * 100}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-3 sm:p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <CurrencyDollarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                </div>
              </div>
              <div className="ml-2 sm:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">
                    Paid Books
                  </dt>
                  <dd className="text-base sm:text-lg font-medium text-gray-900">
                    {stats.paid}
                  </dd>
                  {stats.total > 0 && (
                    <div className="mt-1">
                      <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                        <div
                          className="bg-purple-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${(stats.paid / stats.total) * 100}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-3 sm:p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                  <TagIcon className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
                </div>
              </div>
              <div className="ml-2 sm:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">
                    Categories
                  </dt>
                  <dd className="text-base sm:text-lg font-medium text-gray-900">
                    {stats.categories}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      {existingCategories.length > 0 && (
        <div className="bg-white shadow rounded-lg border border-gray-200 p-4 sm:p-6 mb-6">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">
            Category Distribution
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {existingCategories.map((category) => {
              const categoryCount = books.filter(
                (book) => book.category === category
              ).length;
              const percentage = ((categoryCount / books.length) * 100).toFixed(
                1
              );
              return (
                <div
                  key={category}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center">
                    <TagIcon className="h-4 w-4 text-purple-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">
                      {category}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      {categoryCount} books
                    </span>
                    <span className="text-xs text-gray-400">
                      ({percentage}%)
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters and Controls */}
      <div className="bg-white shadow rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-col space-y-4">
          {/* Search */}
          <div className="w-full">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search books..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            {/* Category Filter */}
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setSubcategoryFilter("all"); // Reset subcategory when category changes
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Subcategory Filter */}
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={subcategoryFilter}
                onChange={(e) => setSubcategoryFilter(e.target.value)}
                disabled={categoryFilter === "all"}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="all">All Subcategories</option>
                {categoryFilter !== "all" &&
                  categoryFilter !== "custom" &&
                  getBookSubcategoryOptionsWithCustom(categoryFilter).map(
                    (subcategory) => (
                      <option key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </option>
                    )
                  )}
              </select>
            </div>

            {/* Price Filter */}
            <div className="flex items-center space-x-2">
              <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
              <select
                value={priceFilter}
                onChange={(e) => setPriceFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Prices</option>
                <option value="free">Free</option>
                <option value="paid">Paid</option>
                <option value="under10">Under GHS 10</option>
                <option value="under25">Under GHS 25</option>
              </select>
            </div>

            {/* Sort */}
            <div className="flex items-center space-x-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="title">Title</option>
                <option value="author">Author</option>
                <option value="amount">Price</option>
                <option value="category">Category</option>
              </select>
              <button
                onClick={() =>
                  setSortDirection(sortDirection === "asc" ? "desc" : "asc")
                }
                className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {sortDirection === "asc" ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-1 bg-gray-100 rounded-md p-1">
              <button
                onClick={() => setViewMode("cards")}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === "cards"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === "table"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <ListBulletIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Page Size Selector */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Show:</span>
              <select
                value={booksPerPage}
                onChange={(e) => {
                  const newPageSize = parseInt(e.target.value);
                  setBooksPerPage(newPageSize);
                  setCurrentPage(1); // Reset to first page when changing page size
                }}
                className="border border-gray-300 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={12}>12 per page</option>
                <option value={24}>24 per page</option>
                <option value={36}>36 per page</option>
                <option value={48}>48 per page</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results Count and Quick Actions */}
      <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="flex flex-col space-y-1">
          <p className="text-sm text-gray-600">
            Showing {currentBooks.length} of {sortedBooks.length} books
          </p>
          {totalPages > 1 && (
            <p className="text-xs text-gray-500">
              Page {currentPage} of {totalPages} • {booksPerPage} books per page
            </p>
          )}
        </div>

        {sortedBooks.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
            <span className="text-sm text-gray-500">Quick actions:</span>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <button
                onClick={() => {
                  setPriceFilter("free");
                  setCategoryFilter("all");
                  setSubcategoryFilter("all");
                  setSearchTerm("");
                }}
                className="inline-flex items-center justify-center px-3 py-1.5 border border-green-300 rounded-full text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 transition-colors"
              >
                Show Free Books
              </button>
              <button
                onClick={() => {
                  setPriceFilter("paid");
                  setCategoryFilter("all");
                  setSubcategoryFilter("all");
                  setSearchTerm("");
                }}
                className="inline-flex items-center justify-center px-3 py-1.5 border border-blue-300 rounded-full text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                Show Paid Books
              </button>
              <button
                onClick={() => {
                  if (sortedBooks.length > 0) {
                    setSelectedBookForAssignment(sortedBooks[0]);
                    setUserAssignmentModalOpen(true);
                  }
                }}
                className="inline-flex items-center justify-center px-3 py-1.5 border border-purple-300 rounded-full text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 transition-colors"
              >
                <UserIcon className="h-3 w-3 mr-1" />
                Assign Users
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Books Display */}
      {currentBooks.length === 0 ? (
        <div className="text-center py-16 sm:py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="max-w-md mx-auto px-6">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpenIcon className="h-12 w-12 text-blue-500" />
            </div>

            {searchTerm ||
            categoryFilter !== "all" ||
            subcategoryFilter !== "all" ||
            priceFilter !== "all" ? (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  No books match your filters
                </h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Try adjusting your search terms or filters to find what you're
                  looking for.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setCategoryFilter("all");
                      setSubcategoryFilter("all");
                      setPriceFilter("all");
                    }}
                    className="inline-flex items-center justify-center px-6 py-3 border border-gray-200 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    Clear All Filters
                  </button>
                  <button
                    onClick={handleAdd}
                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add New Book
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Your library is empty
                </h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Start building your digital library by adding your first book.
                  You can add books with covers, descriptions, and set pricing.
                </p>
                <button
                  onClick={handleAdd}
                  className="inline-flex items-center justify-center px-8 py-4 border border-transparent rounded-xl shadow-sm text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Your First Book
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div>
          {viewMode === "cards" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-6 lg:gap-8">
              {currentBooks.map(renderBookCard)}
            </div>
          ) : (
            renderBookTable()
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-medium">{indexOfFirstBook + 1}</span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(indexOfLastBook, sortedBooks.length)}
                    </span>{" "}
                    of <span className="font-medium">{sortedBooks.length}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px pagination-controls"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => {
                      // Show limited page numbers for better UX
                      const pageNumber = i + 1;
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 &&
                          pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={i}
                            onClick={() => goToPage(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium pagination-page ${
                              currentPage === pageNumber
                                ? "z-10 bg-blue-500 text-white focus-visible:outline-none active"
                                : "bg-white text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return (
                          <span
                            key={i}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                    </button>
                  </nav>

                  {/* Go to page input */}
                  <div className="ml-4 flex items-center space-x-2">
                    <span className="text-sm text-gray-500">Go to:</span>
                    <input
                      type="number"
                      min="1"
                      max={totalPages}
                      value={currentPage}
                      onChange={(e) => {
                        const page = parseInt(e.target.value);
                        if (page >= 1 && page <= totalPages) {
                          goToPage(page);
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          const page = parseInt(e.target.value);
                          if (page >= 1 && page <= totalPages) {
                            goToPage(page);
                          }
                        }
                      }}
                      className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-sm text-gray-500">
                      of {totalPages}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Book Modal */}
      <BookModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        document={selectedDocument}
        mode={modalMode}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Book"
        message="Are you sure you want to delete this book? This action cannot be undone."
        loading={deleteLoading}
      />

      {/* User Assignment Modal */}
      <UserAssignmentModal
        isOpen={userAssignmentModalOpen}
        onClose={handleUserAssignmentModalClose}
        book={selectedBookForAssignment}
      />

      {/* Book Reviews Modal */}
      <BookReviewsModal
        isOpen={reviewsModalOpen}
        onClose={handleReviewsModalClose}
        book={selectedBookForReviews}
      />
    </div>
  );
};

export default BooksPage;
